import React, { useEffect, useReducer, useRef } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000Label from "../../../u2000Core/components/formControls/label/U2000Label";
import U2000InputText from "../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000RadioGroupString from "../../../u2000Core/components/formControls/radio/U2000RadioGroupString";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { U3104PeriodEndPattern } from "../../U3104Service";
import { U3104_PERIOD_END_PATTERN_DEFAULT } from "../../U3104Service";

export interface U3104NewAccountingYearDialogProps {
  isOpen: boolean;
  /** Chaîne formulaire (`yyyy-MM-dd` ou ISO) — la génération des périodes n’utilise que la partie date. */
  onApply: (yearCode: string, firstPeriodDay: string, periodEndPattern: U3104PeriodEndPattern) => void;
  onCancel: () => void;
  t: TFunction;
  NS: string;
}

export type U3104NewAccountingYearDialogRow = {
  yearCode: string;
  firstPeriodDay: string;
  periodEndPattern: U3104PeriodEndPattern;
};

function parseUtcDateOnly(value: string): Date | null {
  const head = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null;
  const [ys, ms, ds] = head.split("-");
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

function emptyDialogRow(): U3104NewAccountingYearDialogRow {
  return { yearCode: "", firstPeriodDay: "", periodEndPattern: U3104_PERIOD_END_PATTERN_DEFAULT };
}

function normalizePeriodEndPattern(v: string | undefined): U3104PeriodEndPattern {
  return v === "startPlusMonthMinusOneDay" ? "startPlusMonthMinusOneDay" : "secondSunday";
}

const dlgInitialFormState: U2000FormState<U3104NewAccountingYearDialogRow> = {
  ...u2000IntFormState,
  current: emptyDialogRow(),
  old: null as unknown as U3104NewAccountingYearDialogRow,
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
};

const U3104NewAccountingYearDialog: React.FC<U3104NewAccountingYearDialogProps> = ({ isOpen, onApply, onCancel, t, NS }) => {
  const [dialogFormState, dialogFormDispatch] = useReducer(u2000FormState, dlgInitialFormState);
  const wasOpenRef = useRef(false);

  const dlgForm = useU2000FormStateHandlers<U3104NewAccountingYearDialogRow>({
    formState: dialogFormState,
    formStateDispatch: dialogFormDispatch,
    businessHandlers: {},
  });

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      dlgForm.setEditMode(U2000FormStateEnumEditMode.EDIT);
      dlgForm.setData(emptyDialogRow());
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- réinitialiser à la transition fermé → ouvert uniquement
  }, [isOpen]);

  const row = dlgForm.getData();
  const y = row?.yearCode?.trim() ?? "";
  const isValid = Boolean(y && y.length <= 50 && parseUtcDateOnly(row?.firstPeriodDay ?? "") !== null);
  const periodEndPatternUi = normalizePeriodEndPattern(row?.periodEndPattern);

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      dlgForm.setData(emptyDialogRow());
      onCancel();
      return;
    }
    const d = parseUtcDateOnly(row?.firstPeriodDay ?? "");
    if (!y || y.length > 50 || !d) return;
    onApply(y, row.firstPeriodDay.trim(), normalizePeriodEndPattern(row.periodEndPattern));
    dlgForm.setData(emptyDialogRow());
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      title={t("lblNewAccountingYearDlgTitle", { ns: NS })}
      onClose={handleClose}
      isValid={isValid}
      width="max-w-lg"
      contentOverflowVisible
    >
      <div className="space-y-4 px-1 py-2">
        <p className="text-sm text-gray-600">
          {periodEndPatternUi === "secondSunday"
            ? t("msgNewAccountingYearDlgHelpSecondSunday", { ns: NS })
            : t("msgNewAccountingYearDlgHelpStartPlusMonthMinusOneDay", { ns: NS })}
        </p>
        <div className="flex flex-col gap-1.5">
          <U2000Label htmlFor="yearCode" label={t("lblYearCode", { ns: NS })} required />
          <U2000InputText
            hideLabel
            formStateHandlers={dlgForm as never}
            fieldName="yearCode"
            label={t("lblYearCode", { ns: NS })}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <U2000InputCalendarCustom
            formStateHandlers={dlgForm as never}
            fieldName="firstPeriodDay"
            label={t("lblFirstAccountingMonthDay", { ns: NS })}
            required
            dateFormat="yyyy-MM-dd"
            popupZIndex={10050}
            popupPosition="auto"
          />
        </div>
        <div className="flex flex-col gap-2 pt-3">
          <U2000RadioGroupString
            titleGroup={t("lblPeriodEndPattern", { ns: NS })}
            orientation="vertical"
            formStateHandlers={dlgForm as never}
            fieldName="periodEndPattern"
            options={[
              { value: "secondSunday", label: t("optPeriodEndSecondSunday", { ns: NS }) },
              { value: "startPlusMonthMinusOneDay", label: t("optPeriodEndStartPlusMonthMinusOneDay", { ns: NS }) },
            ]}
          />
          <p className="text-xs text-gray-500">
            {periodEndPatternUi === "secondSunday"
              ? t("msgPeriodEndPatternHintSecondSunday", { ns: NS })
              : t("msgPeriodEndPatternHintStartPlusMonthMinusOneDay", { ns: NS })}
          </p>
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3104NewAccountingYearDialog;
